import { useState, useCallback } from 'react';
import { ReferenceImage } from '../services/imageGenerationApi';
import { LibraryItem } from '../types';

export interface UseReferenceImagesReturn {
  referenceImages: ReferenceImage[];
  addReferenceImage: (image: ReferenceImage) => void;
  addFromLibrary: (item: LibraryItem) => void;
  removeReferenceImage: (index: number) => void;
  clearReferenceImages: () => void;
  canAddMore: boolean;
  maxImages: number;
}

export const useReferenceImages = (maxImages: number = 3): UseReferenceImagesReturn => {
  const [referenceImages, setReferenceImages] = useState<ReferenceImage[]>([]);

  const addReferenceImage = useCallback((image: ReferenceImage) => {
    setReferenceImages(prev => {
      if (prev.length >= maxImages) return prev;
      return [...prev, image];
    });
  }, [maxImages]);

  const addFromLibrary = useCallback((item: LibraryItem) => {
    const referenceImage: ReferenceImage = {
      uri: item.url,
      tag: 'library',
      url: item.url,
      name: item.name || 'Library Image',
      uploadedAt: new Date().toISOString()
    };
    addReferenceImage(referenceImage);
  }, [addReferenceImage]);

  const removeReferenceImage = useCallback((index: number) => {
    setReferenceImages(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearReferenceImages = useCallback(() => {
    setReferenceImages([]);
  }, []);

  const canAddMore = referenceImages.length < maxImages;

  return {
    referenceImages,
    addReferenceImage,
    addFromLibrary,
    removeReferenceImage,
    clearReferenceImages,
    canAddMore,
    maxImages
  };
};
